import React, { useEffect, useState } from 'react';
import { useWebVitals } from '@/hooks/useWebVitals';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Activity, Zap, Image, Network } from 'lucide-react';

import { log } from "@/utils/log";

interface PerformanceMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  threshold: { good: number; poor: number };
}

interface SEOPerformanceData {
  coreWebVitals: PerformanceMetric[];
  technicalSEO: {
    structuredData: boolean;
    metaTags: boolean;
    imageOptimization: boolean;
    mobileOptimization: boolean;
  };
  resourceOptimization: {
    imagesOptimized: number;
    totalImages: number;
    criticalResourcesPreloaded: boolean;
    serviceWorkerActive: boolean;
  };
}

const getRating = (value: number, threshold: { good: number; poor: number }): 'good' | 'needs-improvement' | 'poor' => {
  if (value <= threshold.good) return 'good';
  if (value <= threshold.poor) return 'needs-improvement';
  return 'poor';
};

const getRatingColor = (rating: string) => {
  switch (rating) {
    case 'good': return 'bg-green-500';
    case 'needs-improvement': return 'bg-yellow-500';
    case 'poor': return 'bg-red-500';
    default: return 'bg-gray-500';
  }
};

export const SEOPerformanceMonitor: React.FC = () => {
  const [performanceData, setPerformanceData] = useState<SEOPerformanceData | null>(null);
  const [showMonitor, setShowMonitor] = useState(false);

  useWebVitals({
    reportWebVitals: (metric) => {
      // Update performance metrics as they come in
      log.log('Web Vital:', metric);
    },
    debug: process.env.NODE_ENV === 'development'
  });

  useEffect(() => {
    // Only show monitor in development mode OR when explicitly enabled
    const isDevelopment = process.env.NODE_ENV === 'development';
    const isExplicitlyEnabled = localStorage.getItem('show-performance-monitor') === 'true';
    const shouldShow = isDevelopment && isExplicitlyEnabled; // Both conditions must be true
    setShowMonitor(shouldShow);

    if (shouldShow) {
      analyzePerformance();
    }
  }, []);

  const analyzePerformance = async () => {
    // Analyze Core Web Vitals
    const coreWebVitals: PerformanceMetric[] = [];
    
    // Get LCP (Largest Contentful Paint)
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          if (lastEntry) {
            coreWebVitals.push({
              name: 'LCP',
              value: lastEntry.startTime,
              rating: getRating(lastEntry.startTime, { good: 2500, poor: 4000 }),
              threshold: { good: 2500, poor: 4000 }
            });
          }
        });
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        log.warn('LCP observation failed:', e);
      }
    }

    // Analyze technical SEO
    const technicalSEO = {
      structuredData: !!document.querySelector('script[type="application/ld+json"]'),
      metaTags: !!(document.querySelector('meta[name="description"]') && document.querySelector('title')),
      imageOptimization: analyzeImageOptimization(),
      mobileOptimization: !!document.querySelector('meta[name="viewport"]')
    };

    // Analyze resource optimization
    const images = document.querySelectorAll('img');
    const optimizedImages = Array.from(images).filter(img => 
      img.loading === 'lazy' || img.getAttribute('srcset')
    ).length;

    const resourceOptimization = {
      imagesOptimized: optimizedImages,
      totalImages: images.length,
      criticalResourcesPreloaded: !!document.querySelector('link[rel="preload"]'),
      serviceWorkerActive: 'serviceWorker' in navigator && !!(await navigator.serviceWorker.getRegistration())
    };

    setPerformanceData({
      coreWebVitals,
      technicalSEO,
      resourceOptimization
    });
  };

  const analyzeImageOptimization = (): boolean => {
    const images = document.querySelectorAll('img');
    const optimizedCount = Array.from(images).filter(img => {
      const src = img.src;
      const hasLazyLoading = img.loading === 'lazy';
      const hasSrcSet = !!img.srcset;
      const hasAltText = !!img.alt;
      const hasOptimizedFormat = src.includes('.webp') || src.includes('w_auto') || src.includes('f_auto');
      
      return hasLazyLoading && hasSrcSet && hasAltText && hasOptimizedFormat;
    }).length;

    return images.length > 0 ? (optimizedCount / images.length) > 0.8 : true;
  };

  // Hide by default - only show in development AND when explicitly enabled
  if (process.env.NODE_ENV !== 'development' || !localStorage.getItem('show-performance-monitor')) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 max-h-96 overflow-y-auto">
      <Card className="shadow-lg border-2">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              <CardTitle className="text-sm">SEO Performance</CardTitle>
            </div>
            <button
              onClick={() => setShowMonitor(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              ×
            </button>
          </div>
          <CardDescription className="text-xs">
            Real-time performance and SEO monitoring
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Core Web Vitals */}
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
              <Zap className="h-3 w-3" />
              Core Web Vitals
            </h4>
            <div className="space-y-2">
              {performanceData.coreWebVitals.map((metric) => (
                <div key={metric.name} className="flex items-center justify-between text-xs">
                  <span>{metric.name}</span>
                  <div className="flex items-center gap-2">
                    <span>{Math.round(metric.value)}ms</span>
                    <Badge 
                      variant="secondary" 
                      className={`h-4 text-white ${getRatingColor(metric.rating)}`}
                    >
                      {metric.rating}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Technical SEO */}
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
              <Network className="h-3 w-3" />
              Technical SEO
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.entries(performanceData.technicalSEO).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <span className="capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                  <Badge variant={value ? "default" : "destructive"} className="h-4">
                    {value ? "✓" : "✗"}
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Resource Optimization */}
          <div>
            <h4 className="text-sm font-medium mb-2 flex items-center gap-1">
              <Image className="h-3 w-3" />
              Resources
            </h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span>Images Optimized</span>
                <span>
                  {performanceData.resourceOptimization.imagesOptimized}/
                  {performanceData.resourceOptimization.totalImages}
                </span>
              </div>
              <Progress 
                value={(performanceData.resourceOptimization.imagesOptimized / 
                        Math.max(performanceData.resourceOptimization.totalImages, 1)) * 100} 
                className="h-2"
              />
              <div className="flex items-center justify-between text-xs">
                <span>Service Worker</span>
                <Badge 
                  variant={performanceData.resourceOptimization.serviceWorkerActive ? "default" : "destructive"} 
                  className="h-4"
                >
                  {performanceData.resourceOptimization.serviceWorkerActive ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};